import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowsPointingOutIcon,
  PhotoIcon,
  TruckIcon,
  SwatchIcon,
} from "@heroicons/react/24/outline";
import { supabase } from "../../supabase";
import { withoutRetiredTraits } from "../../constants/traits";
import { SparkleIcon } from "../SparkleIcon";
import Button from "../Button";

const BROWSE_TYPES = {
  room: "recommended_room",
  mood: "color_psychology_primary_trait",
};

// The footer renders on every route, so the category lookup is cached at module
// scope and shared across navigations instead of refetching per page.
let browseCategoriesCache = null;
let browseCategoriesRequest = null;

async function loadBrowseCategories() {
  if (browseCategoriesCache) return browseCategoriesCache;
  if (browseCategoriesRequest) return browseCategoriesRequest;

  browseCategoriesRequest = supabase
    .from("collection_metadata")
    .select("id, collection_type, value")
    .in("collection_type", Object.values(BROWSE_TYPES))
    .neq("value", "Undefined")
    .neq("value", "undefined")
    .then(({ data, error }) => {
      if (error) throw error;
      browseCategoriesCache = withoutRetiredTraits(data);
      return browseCategoriesCache;
    })
    .finally(() => {
      browseCategoriesRequest = null;
    });

  return browseCategoriesRequest;
}

const PROMISES = [
  { icon: ArrowsPointingOutIcon, text: "Flexible size, frame and mount options" },
  { icon: PhotoIcon, text: "High definition, print-ready giclée quality" },
  { icon: TruckIcon, text: "Fulfilled locally for Great Britain and US delivery" },
  { icon: SwatchIcon, text: "Printed to order, so nothing goes to waste" },
];

const FooterColumn = ({ title, links }) => (
  <div>
    <h2 className="border-b border-white/15 pb-3 font-serif text-base font-bold text-white">
      {title}
    </h2>
    <ul className="mt-4 space-y-3">
      {links.map((link) => (
        <li key={link.href}>
          <Link
            to={link.href}
            className="text-sm text-slate-300 transition hover:text-white"
          >
            {link.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const BrowseCloud = ({ title, values, slug }) => {
  if (values.length === 0) return null;

  return (
    <div>
      <h2 className="font-serif text-base font-bold text-white">{title}</h2>
      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
        {values.map((value) => (
          <Link
            key={value}
            to={`/collections/${slug}/${String(value)
              .toLowerCase()
              .replace(/\s+/g, "-")}`}
            className="text-sm text-slate-400 transition hover:text-white"
          >
            {value}
          </Link>
        ))}
      </div>
    </div>
  );
};

const Footer = () => {
  const [categories, setCategories] = useState(browseCategoriesCache || []);

  useEffect(() => {
    if (window.__CREATE_PRINTS_PRERENDER__) return undefined;

    let cancelled = false;

    loadBrowseCategories()
      .then((data) => {
        if (!cancelled) setCategories(data);
      })
      .catch((err) => {
        // A failed lookup just hides the browse clouds; the rest of the footer
        // is static and still renders.
        console.error("Error loading footer categories:", err);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const valuesFor = (type) =>
    categories
      .filter((category) => category.collection_type === type)
      .map((category) => category.value)
      .sort((a, b) => String(a).localeCompare(String(b)));

  return (
    <footer className="mt-auto bg-slate-950 text-slate-300">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid grid-cols-1 gap-8 border-b border-white/10 py-10 sm:grid-cols-2 lg:grid-cols-4">
          {PROMISES.map(({ icon: Icon, text }) => (
            <div key={text} className="flex items-start gap-3">
              <Icon className="h-6 w-6 flex-none text-white" />
              <p className="text-sm text-slate-300">{text}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-8 py-12 lg:grid-cols-4">
          <FooterColumn
            title="Shop"
            links={[
              { href: "/prints", label: "All prints" },
              { href: "/generate-print", label: "Create your own" },
              { href: "/collections", label: "Collections" },
              { href: "/collections/recommended-room", label: "Shop by room" },
              {
                href: "/collections/color-psychology-primary-trait",
                label: "Shop by mood",
              },
            ]}
          />
          <FooterColumn
            title="Account"
            links={[
              { href: "/account", label: "Your account" },
              { href: "/account/orders", label: "Your orders" },
              { href: "/login", label: "Login / Register" },
            ]}
          />
          <FooterColumn
            title="Help"
            links={[
              { href: "/articles", label: "Articles" },
              { href: "/about", label: "About Create Prints" },
              { href: "/contact", label: "Customer support" },
              { href: "/developers", label: "Developers" },
              { href: "/shipping-policy", label: "Delivery" },
              { href: "/returns-policy", label: "Returns" },
              { href: "/contact#report-content", label: "Report content" },
            ]}
          />
          <FooterColumn
            title="Legal"
            links={[
              { href: "/terms", label: "Terms of service and sale" },
              { href: "/privacy-policy", label: "Privacy policy" },
              { href: "/returns-policy", label: "Returns policy" },
              { href: "/shipping-policy", label: "Shipping policy" },
            ]}
          />
        </div>

        <div className="space-y-8 border-t border-white/10 py-12">
          <BrowseCloud
            title="Browse art by room"
            values={valuesFor(BROWSE_TYPES.room)}
            slug="recommended-room"
          />
          <BrowseCloud
            title="Browse art by mood"
            values={valuesFor(BROWSE_TYPES.mood)}
            slug="color-psychology-primary-trait"
          />
        </div>

        <div className="flex flex-col gap-6 border-t border-white/10 py-10 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-2xl space-y-4">
            <Link to="/" className="flex items-center gap-2">
              <SparkleIcon className="h-8 w-8 text-white" />
              <span className="font-serif text-xl font-bold text-white">
                Create Prints
              </span>
            </Link>
            <p className="text-sm text-slate-400">
              Create Prints is operated by Viable Digital Limited, registered in
              England and Wales (company number 13599956). Registered office:
              Office 4, 8–9 Rodney Road, Portsmouth, England, PO4 8BF.
            </p>
          </div>

          <div className="lg:text-right">
            <h2 className="font-serif text-base font-bold text-white">
              Use with your AI assistant
            </h2>
            <p className="mt-2 max-w-xs text-sm text-slate-400 lg:ml-auto">
              Browse and order prints straight from Claude or ChatGPT.
            </p>
            <Button
              url="/mcp/connect"
              variant="inverseOutline"
              size="sm"
              className="mt-4"
            >
              Connect Create Prints
            </Button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
